import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { App, Button, Segmented, Space, Tag, Tooltip, Typography } from 'antd';
import {
  AuditOutlined,
  BranchesOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  ExclamationCircleFilled,
  InfoCircleFilled,
  PartitionOutlined,
  PlayCircleOutlined,
  ReadOutlined,
  RedoOutlined,
  SafetyCertificateOutlined,
  SaveOutlined,
  UndoOutlined,
  ColumnWidthOutlined,
} from '@ant-design/icons';
import type { Connection } from '@xyflow/react';
import { getSample } from '@/flow-ir/samples';
import { parseFlowYaml, previewDiagnostics } from '@/flow-ir/parse';
import { toRenderGraph } from '@/flow-ir/toReactFlow';
import { edgeId, isVariant, type EdgeKind, type FlowEdge, type FlowNode } from '@/flow-ir/types';
import { toJsonSource } from '@/flow-ir/toIR';
import { addEdge, addNode, deleteEdge, deleteNode, freshNodeId, makeEdge, setNodeStage } from '@/flow-ir/mutations';
import { fromPreview, fromValidation, indexDiagnostics, type UiDiagnostic } from '@/flow-ir/diagnostics';
import { indexDiff } from '@/flow-ir/diff';
import type { LifecycleStage } from '@/theme/tokens';
import type { XY } from '@/store/designerSlice';
import {
  clearPosition,
  commitEdit,
  loadFlow,
  markSaved,
  redo,
  resetPositions,
  selectEdge,
  selectNode,
  setPosition,
  undo,
} from '@/store/designerSlice';
import { toggleDataLayer, setDataLayerVisible } from '@/store/uiSlice';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { getFlowDetail, getFlowIr, createFlow, createFlowVersion, diffFlow, validateFlow } from '@/api/flows';
import { listCapabilityFamilies } from '@/api/capabilities';
import { normalizeApiError, isNotAvailable } from '@/api/errors';
import type { DiffResult, PublishResult } from '@/api/types';
import { usePageTitle } from '@/components/pageTitle';
import { EmptyState } from '@/components/EmptyState';
import { EDU, trackMilestoneOnce } from '@/edu/eduClient';
import { DATA_TOUR } from '@/edu/tourModel';
import { DesignerCanvas, type CanvasFocusTarget } from '@/designer/DesignerCanvas';
import { tidyLayout } from '@/designer/autoLayout';
import { mergeWithLastValidation, overlayFor, type DesignerDiagnostic } from '@/designer/diagOverlay';
import { loadMinimapVisible, saveMinimapVisible } from '@/designer/canvasPrefs';
import { PipelineHint } from '@/designer/PipelineHint';
import { NodeInspector } from '@/designer/NodeInspector';
import { EdgeInspector } from '@/designer/EdgeInspector';
import { Legend } from '@/designer/Legend';
import { Palette } from '@/designer/Palette';
import { buildPalette, FALLBACK_FAMILIES, type PaletteEntry } from '@/designer/paletteModel';
import { ProblemsPanel } from '@/designer/ProblemsPanel';
import { DiffPanel } from '@/designer/DiffPanel';
import { EdgeKindPicker, type EdgeDraftExtra } from '@/designer/EdgeKindPicker';
import { PublishDrawer } from '@/designer/PublishDrawer';
import { StartRunDrawer } from '@/runs/StartRunDrawer';
import type { FlowDefinition } from '@/flow-ir/types';

const { Text } = Typography;

/** A blank base scaffold for a brand-new flow (Save → createFlow). */
function newScaffold(): FlowDefinition {
  const slug = `draft-${Math.random().toString(36).slice(2, 8)}`;
  return {
    schema: 'flow-definition.v1',
    id: slug,
    version: 1,
    metadata: { name: 'Untitled flow', stages: ['ONBOARDING'] },
    nodes: [],
    edges: [],
  };
}

export function DesignerPage() {
  const { flowId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { message } = App.useApp();

  const theme = useAppSelector((s) => s.ui.theme);
  const dataLayerVisible = useAppSelector((s) => s.ui.dataLayerVisible);
  const d = useAppSelector((s) => s.designer);
  const def = d.present;
  const editable = d.editable;
  const canUndo = d.past.length > 0;
  const canRedo = d.future.length > 0;

  const [palette, setPalette] = useState<PaletteEntry[]>(() => buildPalette(FALLBACK_FAMILIES));
  const [familiesLoading, setFamiliesLoading] = useState(false);
  const [placing, setPlacing] = useState<PaletteEntry | null>(null);
  const [pendingConn, setPendingConn] = useState<{ conn: Connection; kind: EdgeKind } | null>(null);
  const [diagnostics, setDiagnostics] = useState<DesignerDiagnostic[]>([]);
  const [serverValidated, setServerValidated] = useState(false);
  /** The last server validation's findings — kept (stale-marked) across edits until re-validate. */
  const lastValidationRef = useRef<UiDiagnostic[] | null>(null);
  /** Node positions captured just before the last Tidy (explicit revert — positions never enter the IR undo stack). */
  const [tidyRevert, setTidyRevert] = useState<Record<string, XY> | null>(null);
  const [minimapVisible, setMinimapVisible] = useState<boolean>(loadMinimapVisible);
  const [focusTarget, setFocusTarget] = useState<CanvasFocusTarget | null>(null);
  const focusNonce = useRef(0);
  const [diffResult, setDiffResult] = useState<DiffResult | null>(null);
  const [bottom, setBottom] = useState<'problems' | 'diff' | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  /** OPSFL-FLOWOPS-1 A1: the Start-run drawer over the published version (Run… / "Run it now"). */
  const [runOpen, setRunOpen] = useState(false);
  const [publishedVersion, setPublishedVersion] = useState<number | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // ---- capability families → palette ---------------------------------------------------------
  useEffect(() => {
    let cancel = false;
    setFamiliesLoading(true);
    listCapabilityFamilies()
      .then((fs) => {
        if (!cancel) setPalette(buildPalette(fs.length ? fs : FALLBACK_FAMILIES));
      })
      .catch(() => {
        if (!cancel) setPalette(buildPalette(FALLBACK_FAMILIES));
      })
      .finally(() => {
        if (!cancel) setFamiliesLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, []);

  // ---- load the flow (API → editable; sample → read-only; else not-found) --------------------
  useEffect(() => {
    if (!flowId) return;
    let cancel = false;
    setLoadError(null);
    setPublishedVersion(null);
    lastValidationRef.current = null;
    setTidyRevert(null);
    setFocusTarget(null);

    if (flowId === 'new') {
      dispatch(loadFlow({ flowId: 'new', def: newScaffold(), editable: true }));
      return;
    }

    (async () => {
      try {
        const ir = await getFlowIr(flowId);
        const parsed = parseFlowYaml(ir.content);
        if (cancel) return;
        dispatch(loadFlow({ flowId, serverFlowId: ir.ref.id, version: ir.ref.version, def: parsed, editable: true }));
        try {
          const detail = await getFlowDetail(flowId);
          if (!cancel) setPublishedVersion(detail.publishedVersion ?? null);
        } catch {
          /* non-fatal */
        }
      } catch (e) {
        const sample = getSample(flowId);
        if (sample && !cancel) {
          dispatch(loadFlow({ flowId, def: sample.def, editable: false }));
        } else if (!cancel) {
          setLoadError(normalizeApiError(e).message);
        }
      }
    })();

    return () => {
      cancel = true;
    };
  }, [flowId, dispatch]);

  // ---- diagnostics: client preview on every def change; server pass invalidated by edits, but
  // its findings survive STALE-MARKED ("from last validation") until the next Validate/Save ------
  useEffect(() => {
    if (!def) {
      setDiagnostics([]);
      return;
    }
    setDiagnostics(
      mergeWithLastValidation(fromPreview(previewDiagnostics(def)), lastValidationRef.current, def),
    );
    setServerValidated(false);
    setDiffResult(null);
  }, [def]);

  const diagIndex = useMemo(() => indexDiagnostics(diagnostics), [diagnostics]);
  const diffIndex = useMemo(() => (diffResult ? indexDiff(diffResult) : undefined), [diffResult]);

  const graph = useMemo(() => {
    if (!def) return null;
    const g = toRenderGraph(def, {
      dataLayerVisible,
      editable,
      positions: d.positions,
      diagIndex,
      diffIndex,
      selectedNodeId: d.selectedNodeId,
      selectedEdgeId: d.selectedEdgeId,
    });
    // Inline-validation overlay: hover text (code + message), stale marking, clean-state mark.
    return {
      ...g,
      nodes: g.nodes.map((n) => {
        const ov = overlayFor(diagIndex.byNode.get(n.id), serverValidated);
        return ov ? { ...n, data: { ...n.data, ...ov } } : n;
      }),
      edges: g.edges.map((e) => {
        const list = diagIndex.byEdge.get(e.id);
        if (!list?.length || !e.data) return e;
        const ov = overlayFor(list, serverValidated);
        return ov ? { ...e, data: { ...e.data, ...ov, diagCount: list.length } } : e;
      }),
    };
  }, [def, dataLayerVisible, editable, d.positions, diagIndex, diffIndex, d.selectedNodeId, d.selectedEdgeId, serverValidated]);

  const selectedNode = useMemo<FlowNode | null>(
    () => def?.nodes?.find((n) => n.id === d.selectedNodeId) ?? null,
    [def, d.selectedNodeId],
  );
  const selectedEdge = useMemo<FlowEdge | null>(
    () => def?.edges?.find((e) => (e.id ?? edgeId(e)) === d.selectedEdgeId) ?? null,
    [def, d.selectedEdgeId],
  );

  const nodeIds = useMemo(() => def?.nodes?.map((n) => n.id) ?? [], [def]);
  const llmTaskIds = useMemo(() => def?.nodes?.filter((n) => n.kind === 'llmTask').map((n) => n.id) ?? [], [def]);

  const statusOptionsFor = useCallback(
    (sourceId?: string): string[] => {
      const src = def?.nodes?.find((n) => n.id === sourceId);
      if (src?.kind === 'dynamicBranch') return ['COMPLETED', 'EMPTY', 'LIMIT_REACHED', 'FAILED'];
      return src?.outcomes ?? src?.decisionOptions ?? ['COMPLETED', 'FAILED'];
    },
    [def],
  );

  const source = useMemo(() => (def ? toJsonSource(def) : ''), [def]);

  // ---- edit handlers -------------------------------------------------------------------------
  const replaceNode = useCallback(
    (next: FlowNode) => {
      if (!def) return;
      dispatch(commitEdit({ ...def, nodes: (def.nodes ?? []).map((n) => (n.id === next.id ? next : n)) }));
    },
    [def, dispatch],
  );

  const replaceEdge = useCallback(
    (next: FlowEdge) => {
      if (!def || !d.selectedEdgeId) return;
      const pinned = { ...next, id: next.id ?? d.selectedEdgeId };
      dispatch(
        commitEdit({
          ...def,
          edges: (def.edges ?? []).map((e) => ((e.id ?? edgeId(e)) === d.selectedEdgeId ? pinned : e)),
        }),
      );
    },
    [def, d.selectedEdgeId, dispatch],
  );

  const placeArmed = useCallback(
    (stage: LifecycleStage, pos?: XY) => {
      if (!def || !placing) return;
      const id = freshNodeId(def, placing.kind);
      const node = placing.make(id, stage);
      dispatch(commitEdit(addNode(def, node)));
      if (pos) dispatch(setPosition({ id, pos }));
      dispatch(selectNode(id));
      setPlacing(null);
      message.success(`Added ${placing.label} to ${stage}`);
    },
    [def, placing, dispatch, message],
  );

  const dropEntry = useCallback(
    (key: string, stage: LifecycleStage, pos: XY) => {
      if (!def) return;
      const entry = palette.find((p) => p.key === key);
      if (!entry || entry.reserved) return;
      const id = freshNodeId(def, entry.kind);
      dispatch(commitEdit(addNode(def, entry.make(id, stage))));
      dispatch(setPosition({ id, pos }));
      dispatch(selectNode(id));
      message.success(`Added ${entry.label} to ${stage}`);
    },
    [def, palette, dispatch, message],
  );

  const onNodeDragStop = useCallback(
    (id: string, pos: XY, laneStage: LifecycleStage) => {
      if (!def) return;
      const node = def.nodes?.find((n) => n.id === id);
      if (node && node.stage !== laneStage) {
        dispatch(commitEdit(setNodeStage(def, id, laneStage)));
        dispatch(clearPosition(id)); // snap into the new lane's deterministic slot
        message.info(`Moved ${node.name ?? id} to ${laneStage}`);
      } else {
        dispatch(setPosition({ id, pos }));
      }
    },
    [def, dispatch, message],
  );

  const onConnect = useCallback((conn: Connection) => {
    const kind: EdgeKind = conn.sourceHandle?.startsWith('data') ? 'data' : 'sequence';
    setPendingConn({ conn, kind });
  }, []);

  const confirmEdge = useCallback(
    (kind: EdgeKind, extra: EdgeDraftExtra) => {
      if (!def || !pendingConn?.conn.source || !pendingConn.conn.target) {
        setPendingConn(null);
        return;
      }
      const edge = makeEdge(def, pendingConn.conn.source, pendingConn.conn.target, kind, extra);
      dispatch(commitEdit(addEdge(def, edge)));
      dispatch(selectEdge(edge.id ?? edgeId(edge)));
      setPendingConn(null);
    },
    [def, pendingConn, dispatch],
  );

  const deleteSelected = useCallback(() => {
    if (!def || !editable) return;
    if (d.selectedNodeId) {
      const node = def.nodes?.find((n) => n.id === d.selectedNodeId);
      dispatch(commitEdit(deleteNode(def, d.selectedNodeId)));
      dispatch(clearPosition(d.selectedNodeId));
      dispatch(selectNode(null));
      message.success(`Deleted ${node?.name ?? d.selectedNodeId}`);
    } else if (d.selectedEdgeId) {
      dispatch(commitEdit(deleteEdge(def, d.selectedEdgeId)));
      dispatch(selectEdge(null));
    }
  }, [def, editable, d.selectedNodeId, d.selectedEdgeId, dispatch, message]);

  /** Pan/zoom the canvas to an element and select it (Problems/Diff row click-to-focus). */
  const panTo = useCallback((id: string, kind: 'node' | 'edge') => {
    setFocusTarget({ id, kind, nonce: ++focusNonce.current });
  }, []);

  const focusDiagnostic = useCallback(
    (diag: UiDiagnostic) => {
      if (diag.nodeId) {
        dispatch(selectNode(diag.nodeId));
        panTo(diag.nodeId, 'node');
      } else if (diag.edgeId) {
        dispatch(selectEdge(diag.edgeId));
        panTo(diag.edgeId, 'edge');
      }
    },
    [dispatch, panTo],
  );

  const focusLocus = useCallback(
    (locus: string) => {
      if (def?.nodes?.some((n) => n.id === locus)) {
        dispatch(selectNode(locus));
        panTo(locus, 'node');
      } else {
        dispatch(selectEdge(locus));
        panTo(locus, 'edge');
      }
    },
    [def, dispatch, panTo],
  );

  // ---- tidy layout (lane-aware auto-arrange) + explicit revert --------------------------------
  const doTidy = useCallback(() => {
    if (!def || !editable) return;
    setTidyRevert({ ...d.positions });
    dispatch(resetPositions());
    for (const [id, pos] of Object.entries(tidyLayout(def))) dispatch(setPosition({ id, pos }));
    message.success('Layout tidied — flow-ordered, crossings minimised, every node in its lane');
  }, [def, editable, d.positions, dispatch, message]);

  const revertTidy = useCallback(() => {
    if (!tidyRevert) return;
    dispatch(resetPositions());
    for (const [id, pos] of Object.entries(tidyRevert)) dispatch(setPosition({ id, pos }));
    setTidyRevert(null);
    message.success('Restored the pre-Tidy arrangement');
  }, [tidyRevert, dispatch, message]);

  const toggleMinimap = useCallback(() => {
    setMinimapVisible((v) => {
      saveMinimapVisible(!v);
      return !v;
    });
  }, []);

  // ---- server actions ------------------------------------------------------------------------
  const doSave = useCallback(async () => {
    if (!def) return;
    setBusy('save');
    try {
      const isNew = d.version == null || d.serverFlowId == null || d.flowId === 'new';
      if (isNew) {
        const res = await createFlow(source, 'json');
        dispatch(markSaved({ version: res.version, serverFlowId: res.summary.id }));
        lastValidationRef.current = fromValidation(res.validation);
        setDiagnostics(lastValidationRef.current);
        setServerValidated(true);
        // TTFW milestone (F-ED-01): first flow ever created by this operator.
        trackMilestoneOnce(EDU.flowFirstCreated, 'flow_created');
        message.success(`Created ${res.summary.id} v${res.version}`);
        navigate(`/flows/${res.summary.id}`, { replace: true });
      } else {
        const res = await createFlowVersion(d.serverFlowId!, source, 'json');
        dispatch(markSaved({ version: res.version }));
        lastValidationRef.current = fromValidation(res.validation);
        setDiagnostics(lastValidationRef.current);
        setServerValidated(true);
        message.success(`Saved v${res.version}`);
      }
    } catch (e) {
      const n = normalizeApiError(e);
      if (n.code === 'FLOW_COMPILE_FAILED') {
        setBottom('problems');
        message.error('Compile failed — fix the errors and save again.');
      } else if (isNotAvailable(n)) {
        message.info('The flows API is not live yet (Track A pending). Your edits stay in the canvas.');
      } else {
        message.error(n.message);
      }
    } finally {
      setBusy(null);
    }
  }, [def, source, d.version, d.serverFlowId, d.flowId, dispatch, navigate, message]);

  const doValidate = useCallback(async () => {
    if (!def) return;
    setBusy('validate');
    try {
      const v = await validateFlow(d.serverFlowId ?? def.id, source, 'json');
      lastValidationRef.current = fromValidation(v);
      setDiagnostics(lastValidationRef.current);
      setServerValidated(true);
      setBottom('problems');
    } catch (e) {
      const n = normalizeApiError(e);
      setBottom('problems');
      if (isNotAvailable(n)) message.info('Server validate not live yet — showing the client preview.');
      else message.error(n.message);
    } finally {
      setBusy(null);
    }
  }, [def, source, d.serverFlowId, message]);

  const doDiff = useCallback(async () => {
    if (!def || !d.serverFlowId || d.version == null) {
      message.info('Save the flow before diffing versions.');
      return;
    }
    if (d.dirty) {
      message.info('Save your changes first — diff compares stored versions.');
      return;
    }
    const from = publishedVersion ?? (d.version > 1 ? d.version - 1 : null);
    if (from == null || from === d.version) {
      message.info('No prior version to diff against.');
      return;
    }
    setBusy('diff');
    try {
      const res = await diffFlow(d.serverFlowId, from, d.version);
      setDiffResult(res);
      setBottom('diff');
    } catch (e) {
      const n = normalizeApiError(e);
      if (isNotAvailable(n)) message.info('Diff endpoint not live yet (Track A pending).');
      else message.error(n.message);
    } finally {
      setBusy(null);
    }
  }, [def, d.serverFlowId, d.version, d.dirty, publishedVersion, message]);

  const openPublish = useCallback(() => {
    if (!d.serverFlowId || d.version == null || d.flowId === 'new') {
      message.info('Save the flow before publishing.');
      return;
    }
    if (d.dirty) {
      message.info('Save your changes before publishing.');
      return;
    }
    setPublishOpen(true);
  }, [d.serverFlowId, d.version, d.flowId, d.dirty, message]);

  const onPublished = useCallback((res: PublishResult) => {
    setPublishedVersion(res.ref.version);
    // TTFW milestone (F-ED-01): first flow ever published by this operator.
    trackMilestoneOnce(EDU.flowFirstPublished, 'flow_published');
  }, []);

  /** "Run it now" from the publish drawer / pipeline hint / toolbar: publish → run in one sitting. */
  const openRun = useCallback(() => {
    if (!d.serverFlowId || publishedVersion == null) {
      message.info('Publish the flow before running it — runs execute the signed, published version only.');
      return;
    }
    setPublishOpen(false);
    setRunOpen(true);
  }, [d.serverFlowId, publishedVersion, message]);

  // ---- keyboard ------------------------------------------------------------------------------
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const mod = e.metaKey || e.ctrlKey;
      const k = e.key.toLowerCase();
      if (mod && k === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (editable) dispatch(undo());
      } else if (mod && (k === 'y' || (k === 'z' && e.shiftKey))) {
        e.preventDefault();
        if (editable) dispatch(redo());
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (editable && (d.selectedNodeId || d.selectedEdgeId)) {
          e.preventDefault();
          deleteSelected();
        }
      } else if (e.key === 'Escape') {
        setPlacing(null);
        setPendingConn(null);
        dispatch(selectNode(null));
      } else if (k === 'd') {
        dispatch(toggleDataLayer());
      } else if (k === 't' && !mod) {
        if (editable) doTidy();
      } else if (k === 'm' && !mod) {
        toggleMinimap();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editable, d.selectedNodeId, d.selectedEdgeId, deleteSelected, dispatch, doTidy, toggleMinimap]);

  // Default the data layer off above 15 nodes when a different flow opens (spec §4.5).
  useEffect(() => {
    const n = def?.nodes?.length ?? 0;
    dispatch(setDataLayerVisible(n <= 15));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d.flowId]);

  // ---- title ---------------------------------------------------------------------------------
  const titleNode = useMemo(() => {
    if (!def) return 'Workflow Designer';
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <PartitionOutlined style={{ color: 'var(--ol-primary)' }} />
        <b style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{def.metadata.name}</b>
        <span className="ol-mono" style={{ fontSize: 11.5, color: 'var(--ol-text-tertiary)' }}>
          {def.id} · v{d.version ?? def.version}
        </span>
        {isVariant(def) && (
          <Tag bordered={false} color="geekblue">
            variant
          </Tag>
        )}
        {editable ? (
          d.dirty ? (
            <Tag bordered={false} color="gold">
              unsaved
            </Tag>
          ) : (
            <Tag bordered={false}>draft</Tag>
          )
        ) : (
          <Tag bordered={false} icon={<ReadOutlined />}>
            read-only
          </Tag>
        )}
      </span>
    );
  }, [def, d.version, d.dirty, editable]);
  usePageTitle(titleNode);

  if (loadError && !def) {
    return (
      <EmptyState
        icon={<PartitionOutlined />}
        title="Flow not found"
        description={loadError}
        action={
          <Button type="primary" onClick={() => navigate('/flows')}>
            Back to Flows
          </Button>
        }
      />
    );
  }

  if (!def || !graph) {
    return (
      <EmptyState
        icon={<PartitionOutlined />}
        title="Loading flow…"
        description={`Opening "${flowId}".`}
      />
    );
  }

  const errors = diagIndex.errors;
  const warnings = diagIndex.warnings;
  const infos = diagIndex.infos;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Toolbar */}
      <div
        style={{
          height: 44,
          flex: 'none',
          borderBottom: '1px solid var(--ol-border)',
          background: 'var(--ol-bg-container)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '0 12px',
        }}
      >
        <Space size={4}>
          <Tooltip title="Undo (Ctrl/Cmd-Z)">
            <Button
              type="text"
              size="small"
              icon={<UndoOutlined />}
              disabled={!editable || !canUndo}
              onClick={() => dispatch(undo())}
              aria-label="Undo"
            />
          </Tooltip>
          <Tooltip title="Redo (Ctrl/Cmd-Shift-Z)">
            <Button
              type="text"
              size="small"
              icon={<RedoOutlined />}
              disabled={!editable || !canRedo}
              onClick={() => dispatch(redo())}
              aria-label="Redo"
            />
          </Tooltip>
          <Tooltip title="Tidy layout (T) — lane-aware auto-arrange: nodes keep their lifecycle lane, flow order top-to-bottom, edge crossings minimised. Deterministic; Revert restores the previous arrangement.">
            <Button
              type="text"
              size="small"
              icon={<ColumnWidthOutlined />}
              disabled={!editable}
              onClick={doTidy}
              aria-label="Tidy layout"
              aria-keyshortcuts="T"
            />
          </Tooltip>
          {tidyRevert && (
            <Tooltip title="Restore node positions from before the last Tidy">
              <Button type="text" size="small" onClick={revertTidy} aria-label="Revert tidy">
                Revert
              </Button>
            </Tooltip>
          )}
        </Space>

        <div style={{ width: 1, height: 20, background: 'var(--ol-border)' }} />

        <Button
          size="small"
          icon={<SaveOutlined />}
          type="primary"
          ghost
          disabled={!editable || !d.dirty}
          loading={busy === 'save'}
          onClick={doSave}
          data-tour={DATA_TOUR.save}
        >
          Save
        </Button>
        <Button
          size="small"
          icon={<CheckCircleFilled />}
          loading={busy === 'validate'}
          onClick={doValidate}
          data-tour={DATA_TOUR.validate}
        >
          Validate
        </Button>
        <Button
          size="small"
          icon={<BranchesOutlined />}
          loading={busy === 'diff'}
          onClick={doDiff}
          data-tour={DATA_TOUR.diff}
        >
          Diff
        </Button>
        <Button
          size="small"
          icon={<SafetyCertificateOutlined />}
          type="primary"
          disabled={!editable}
          onClick={openPublish}
          data-tour={DATA_TOUR.publish}
        >
          Publish…
        </Button>
        <Tooltip
          title={
            publishedVersion != null
              ? `Start a run of v${publishedVersion} (the published version)`
              : 'Nothing published yet — runs execute the signed, published version only'
          }
        >
          <Button
            size="small"
            icon={<PlayCircleOutlined />}
            disabled={!editable || publishedVersion == null || !d.serverFlowId}
            onClick={openRun}
            aria-label="Run the published version"
          >
            Run…
          </Button>
        </Tooltip>

        <div style={{ flex: 1 }} />

        {/* The "now what?" answer — always names the next pipeline step (owner feedback
            2026-08-09: after an edit, nothing guided the operator to Save→Validate→Publish). */}
        <PipelineHint
          editable={editable}
          dirty={d.dirty}
          serverValidated={serverValidated}
          errorCount={errors}
          version={d.version ?? 1}
          publishedVersion={publishedVersion}
          onRunNow={openRun}
        />

        {editable && d.dirty && (
          <Tooltip title="Unsaved changes">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span className="ol-dirty-dot" aria-hidden />
              <Text type="secondary" style={{ fontSize: 12 }}>
                unsaved
              </Text>
            </span>
          </Tooltip>
        )}
        {!editable && (
          <Tag bordered={false} icon={<ReadOutlined />}>
            read-only sample
          </Tag>
        )}
      </div>

      {/* Body: palette · canvas · inspector */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {editable && (
          <Palette entries={palette} placingKey={placing?.key ?? null} onArm={setPlacing} loading={familiesLoading} />
        )}
        <div style={{ flex: 1, minWidth: 0, position: 'relative' }} data-tour={DATA_TOUR.canvas}>
          <DesignerCanvas
            nodes={graph.nodes}
            edges={graph.edges}
            layout={graph.layout}
            colorMode={theme}
            editable={editable}
            placing={Boolean(placing)}
            onSelectNode={(id) => dispatch(selectNode(id))}
            onSelectEdge={(id) => dispatch(selectEdge(id))}
            onNodeMove={(id, pos) => dispatch(setPosition({ id, pos }))}
            onNodeDragStop={onNodeDragStop}
            onConnectEdge={onConnect}
            onDropEntry={dropEntry}
            onPlaceInLane={(stage) => placeArmed(stage)}
            onPanePlace={(stage, pos) => placeArmed(stage, pos)}
            onPaneClear={() => {
              dispatch(selectNode(null));
            }}
            minimapVisible={minimapVisible}
            onToggleMinimap={toggleMinimap}
            focusTarget={focusTarget}
          />
        </div>
        {selectedNode ? (
          <NodeInspector
            node={selectedNode}
            editable={editable}
            diagnostics={diagIndex.byNode.get(selectedNode.id) ?? []}
            nodeIds={nodeIds}
            llmTaskIds={llmTaskIds}
            onChange={replaceNode}
            onDelete={deleteSelected}
            onFocusDiagnostic={focusDiagnostic}
            onClose={() => dispatch(selectNode(null))}
          />
        ) : selectedEdge ? (
          <EdgeInspector
            edge={selectedEdge}
            editable={editable}
            statusOptions={statusOptionsFor(selectedEdge.from)}
            diagnostics={diagIndex.byEdge.get(selectedEdge.id ?? edgeId(selectedEdge)) ?? []}
            onChange={replaceEdge}
            onDelete={deleteSelected}
            onClose={() => dispatch(selectEdge(null))}
          />
        ) : null}
      </div>

      {/* Bottom panel: problems / diff */}
      {bottom && (
        <div
          style={{
            height: 200,
            flex: 'none',
            borderTop: '1px solid var(--ol-border)',
            background: 'var(--ol-bg-container)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '4px 10px',
              borderBottom: '1px solid var(--ol-border)',
            }}
          >
            <Segmented
              size="small"
              value={bottom}
              onChange={(v) => setBottom(v as 'problems' | 'diff')}
              options={[
                { label: `Problems${errors + warnings + infos ? ` (${errors + warnings + infos})` : ''}`, value: 'problems' },
                { label: 'Diff', value: 'diff' },
              ]}
            />
            <div style={{ flex: 1 }} />
            <Button type="text" size="small" onClick={() => setBottom(null)}>
              Hide
            </Button>
          </div>
          <div style={{ flex: 1, minHeight: 0 }}>
            {bottom === 'problems' ? (
              <ProblemsPanel diagnostics={diagnostics} serverValidated={serverValidated} onFocus={focusDiagnostic} />
            ) : diffResult ? (
              <DiffPanel result={diffResult} onFocusLocus={focusLocus} />
            ) : (
              <div style={{ padding: 16 }}>
                <Text type="secondary" style={{ fontSize: 12.5 }}>
                  Run <b>Diff</b> to compare this version against the published one.
                </Text>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Status bar */}
      <div
        style={{
          height: 40,
          flex: 'none',
          borderTop: '1px solid var(--ol-border)',
          background: 'var(--ol-bg-container)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '0 12px',
          fontSize: 12.5,
        }}
      >
        <Tag bordered={false} style={{ fontSize: 11, letterSpacing: '0.04em' }}>
          MODE · {editable ? 'EDIT' : 'CANVAS'}
        </Tag>
        <Text type="secondary" className="ol-mono">
          {def.nodes?.length ?? 0} nodes · {def.edges?.length ?? 0} edges
        </Text>
        {(def.metadata.jurisdictions ?? []).map((j) => (
          <Tag key={j} bordered={false} style={{ fontSize: 11 }}>
            {j}
          </Tag>
        ))}

        <div style={{ flex: 1 }} />

        <Button
          type="text"
          size="small"
          onClick={() => setBottom(bottom === 'problems' ? null : 'problems')}
          aria-label="Toggle problems panel"
        >
          <Space size={10}>
            {errors + warnings === 0 && (
              <span style={{ color: 'var(--ol-positive)' }}>
                <CheckCircleFilled /> {serverValidated ? 'compiles' : 'preview ok'}
              </span>
            )}
            {errors > 0 && (
              <span style={{ color: 'var(--ol-negative)' }}>
                <CloseCircleFilled /> {errors}
              </span>
            )}
            {warnings > 0 && (
              <span style={{ color: 'var(--ol-attention)' }}>
                <ExclamationCircleFilled /> {warnings}
              </span>
            )}
            {infos > 0 && (
              <span style={{ color: 'var(--ol-info)' }}>
                <InfoCircleFilled /> {infos}
              </span>
            )}
          </Space>
        </Button>
        <Tooltip title="Toggle the data-dependency layer (D). The data layer never gates.">
          <Segmented
            size="small"
            value={dataLayerVisible ? 'on' : 'off'}
            onChange={(v) => dispatch(setDataLayerVisible(v === 'on'))}
            options={[
              { label: 'Data off', value: 'off' },
              { label: 'Data on', value: 'on' },
            ]}
          />
        </Tooltip>
        <LegendPopover />
      </div>

      {/* Overlays */}
      <EdgeKindPicker
        connection={pendingConn?.conn ?? null}
        defaultKind={pendingConn?.kind ?? 'sequence'}
        statusOptions={statusOptionsFor(pendingConn?.conn.source ?? undefined)}
        onConfirm={confirmEdge}
        onCancel={() => setPendingConn(null)}
      />
      {d.serverFlowId && d.version != null && (
        <PublishDrawer
          open={publishOpen}
          flowId={d.serverFlowId}
          version={d.version}
          publishedVersion={publishedVersion}
          source={source}
          onClose={() => setPublishOpen(false)}
          onPublished={onPublished}
          onRunItNow={openRun}
        />
      )}
      {d.serverFlowId && (
        <StartRunDrawer
          open={runOpen}
          flowId={d.serverFlowId}
          flowName={def?.metadata.name}
          publishedVersion={publishedVersion}
          onClose={() => setRunOpen(false)}
        />
      )}
    </div>
  );
}

/** Small legend popover trigger (kept out of the status-bar JSX for readability). */
function LegendPopover() {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: 'relative' }}>
      <Button type="text" size="small" icon={<AuditOutlined />} onClick={() => setOpen((o) => !o)}>
        Legend
      </Button>
      {open && (
        <div
          style={{
            position: 'absolute',
            bottom: 32,
            right: 0,
            zIndex: 10,
            background: 'var(--ol-bg-elevated)',
            border: '1px solid var(--ol-border)',
            borderRadius: 8,
            padding: 12,
            boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
          }}
          role="dialog"
          aria-label="Canvas legend"
        >
          <Legend />
        </div>
      )}
    </span>
  );
}
